import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Configuration for navbar and logo resizing
const NAVBAR_CONFIG = {
  maxScroll: 300,
  maxNavbarHeight: 100,
  minNavbarHeight: 60,
  maxLogoScale: 1.5,
  minLogoScale: 1,
  scrollTransitionFactor: 1000,
  logoInitialTop: 20,
  logoInitialLeft: 20,
  finalLogoTop: 5,
  finalLogoLeft: 10,
};


const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeSection, setActiveSection] = useState('banner');

  const isHomePage = location.pathname === '/';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Track scroll position to adjust navbar background, logo size, and navbar height
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);

      if (isHomePage) {
        // Find the current active section
        const sections = ['banner', 'about', 'games', 'team', 'contact'];
        const currentSection = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });

        if (currentSection) {
          setActiveSection(currentSection);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen, isMobile]);

  const opacity = Math.min(1, scrollPosition / NAVBAR_CONFIG.maxScroll);
  const backgroundColor = `rgba(0, 0, 0, ${opacity})`;

  const navbarHeight = Math.max(
    NAVBAR_CONFIG.minNavbarHeight,
    NAVBAR_CONFIG.maxNavbarHeight - scrollPosition / 10
  );

  const constrainedScrollPosition = Math.min(scrollPosition, NAVBAR_CONFIG.maxScroll);

  const logoScale = Math.max(
    NAVBAR_CONFIG.minLogoScale,
    NAVBAR_CONFIG.maxLogoScale -
    (constrainedScrollPosition / NAVBAR_CONFIG.maxScroll) *
    (NAVBAR_CONFIG.maxLogoScale - NAVBAR_CONFIG.minLogoScale)
  );

  const logoTop = Math.max(
    NAVBAR_CONFIG.finalLogoTop,
    NAVBAR_CONFIG.logoInitialTop -
    (constrainedScrollPosition / NAVBAR_CONFIG.maxScroll) *
    (NAVBAR_CONFIG.logoInitialTop - NAVBAR_CONFIG.finalLogoTop)
  );

  const logoLeft = Math.max(
    NAVBAR_CONFIG.finalLogoLeft,
    NAVBAR_CONFIG.logoInitialLeft -
    (constrainedScrollPosition / NAVBAR_CONFIG.maxScroll) *
    (NAVBAR_CONFIG.logoInitialLeft - NAVBAR_CONFIG.finalLogoLeft)
  );

  const logoMaxSize = Math.min(navbarHeight * 0.8, NAVBAR_CONFIG.maxLogoScale * 200);
  const logoWidth = logoScale * logoMaxSize;

  // Navigation items
  const navItems = [
    { id: 'banner', label: 'Home', path: '/' },
    { id: 'about', label: 'About', path: '/about' },
    { id: 'games', label: 'Game', path: '/games' },
    { id: 'team', label: 'Team', path: '/team' },
    { id: 'contact', label: 'Contact', path: '/contact' },
    { id: 'privacy', label: 'Privacy Policy', path: '/mgg-privacy-policy' }
  ];

  return (
    <nav
      className={`navigation ${isMenuOpen ? 'active' : ''}`}
      style={{
        backgroundColor,
        height: `${navbarHeight}px`,
        position: 'fixed',
        width: '100%',
        maxWidth: '100%',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        transition: 'all 0.3s ease',
        boxSizing: 'border-box',
        margin: 0,
        padding: '10px 20px'
      }}
    >
      <Link to="/">
        <img
          src="media/logos/mgg-logo-full.png"
          alt='Midnight Ghoul Games Logo'
          width={isMobile ? navbarHeight * 0.8 : logoWidth}
          height={isMobile ? navbarHeight * 0.8 : logoWidth}
          style={{
            position: 'absolute',
            top: isMobile ? '50%' : `${logoTop}px`,
            left: isMobile ? '20px' : `${logoLeft}px`,
            transform: isMobile ? 'translateY(-50%)' : `scale(${logoScale})`,
            transition: 'all 0.3s ease'
          }}
        />
      </Link>
      <div
        className={`menuToggle ${isMenuOpen ? 'active' : ''}`}
        onClick={toggleMenu}
      ></div>
      <ul className={`navigation-list ${isMenuOpen ? 'active' : ''}`}>
        {navItems.map(item => (
          <li key={item.id}>
            <Link
              to={item.path}
              onClick={() => {
                closeMenu();
                if (isHomePage && item.id !== 'privacy') {
                  const element = document.getElementById(item.id);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }
              }}
              className={
                item.id === 'privacy'
                  ? location.pathname === item.path ? 'active' : ''
                  : activeSection === item.id ? 'active' : ''
              }
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default Navigation;
